import { useMemo } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useHoldDetails from "@app-contexts/user/features/basket/data/services/useHoldDetails";
import { ITEM_TYPES_MAP } from "@shared/constants/enums";
import type { GetBasketHoldParamType } from "@app-contexts/user/features/basket/data/types/basket";
import type { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import type { BasketUpgradesResult } from "@shared/modules/basket/domain/services/BasketUpgradesService";

type UseCartSummaryDataOptions = {
  enableItemData?: boolean;
  holdSlug?: string;
  dynamicHoldParams?: GetBasketHoldParamType;
};

type UseCartSummaryDataReturn = {
  hasItems: boolean;
  displayItemGroups: TicketItemGroupType[];
  holdDetailsData: ReturnType<typeof useHoldDetails>["data"];
  basketUpgradesData: BasketUpgradesResult | undefined;
  isBasketUpdating: boolean;
  itemGroups: TicketItemGroupType[];
  basketItemData: ReturnType<typeof usePublicBasket>["basketItemData"];
  isWaitlist: boolean;
  supportedCCTypes: ReturnType<typeof usePublicBasket>["supportedCCTypes"];
  totalItemQuantity: number | undefined;
  basketUpgrades: ReturnType<typeof usePublicBasket>["basketUpgrades"];
};

export function useCartSummaryData({
  enableItemData = false,
  holdSlug,
  dynamicHoldParams,
}: UseCartSummaryDataOptions): UseCartSummaryDataReturn {
  const {
    hasItems,
    itemGroups,
    isBasketUpdating,
    basketItemData,
    basketUpgrades,
    isWaitlist,
    supportedCCTypes,
    totalItemQuantity,
  } = usePublicBasket({ enableItemData });

  const { data: holdDetailsData } = useHoldDetails(
    holdSlug ?? "",
    dynamicHoldParams,
    { enabled: Boolean(holdSlug) }
  );

  const basketUpgradesData = basketUpgrades?.getBasketUpgrades();

  const isProtectionOnlyBasket = useMemo(
    () =>
      (itemGroups ?? []).length > 0 &&
      (itemGroups ?? []).every(
        (item) => item.item_type === ITEM_TYPES_MAP.ITEM_TYPE_PROTECTION.id
      ),
    [itemGroups]
  );

  const displayItemGroups = useMemo(
    () =>
      isProtectionOnlyBasket
        ? itemGroups ?? []
        : (itemGroups ?? []).filter(
            (item) => item.item_type !== ITEM_TYPES_MAP.ITEM_TYPE_PROTECTION.id
          ),
    [itemGroups, isProtectionOnlyBasket]
  );

  return {
    hasItems,
    displayItemGroups,
    holdDetailsData,
    basketUpgradesData,
    isBasketUpdating,
    itemGroups,
    basketItemData,
    isWaitlist,
    supportedCCTypes,
    totalItemQuantity,
    basketUpgrades,
  };
}
